import * as React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';

import { RangeDateTimePicker, IBasicTableColumn, Typography } from 'ui-lib';
import { DATE_TIME_FORMAT, formatDate } from 'common-utils/date-utils';
import { hasContainsIgnoreCase, localeCompare } from 'common-utils/string-utils';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

interface IFilterColumnOptions {
    key: string;
    title: string;
    defaultSortOrder?: 'ascend' | 'descend';
    columnExtraOptions?: Omit<IBasicTableColumn, 'title' | 'key' | 'code' | 'dataIndex'>;
}
export interface IBasicFilterTimeColumnOptions extends IFilterColumnOptions {
    compareKey?: string;
    format?: DATE_TIME_FORMAT;
}
export interface IBasicFilterColumnOptions extends IFilterColumnOptions {
    searchPlaceholder?: string;
    render?: (text: any, record: any) => React.ReactNode;
}
export interface ITruncatedTextColumnOptions extends IBasicFilterColumnOptions {
    maxWidth?: number;
}
export interface IBasicEnumFilterColumnOptions extends IFilterColumnOptions {
    filters: { text: string; value: string }[];
    defaultFilteredValue?: string[];
}
export interface IExtraColumnOptions extends IFilterColumnOptions {
    render: (text: any, record: any) => React.ReactNode;
}
export class TableUtils {
    public static createBasicFilterTimeColumn<T>(options: IBasicFilterTimeColumnOptions): IBasicTableColumn {
        const { key, title, compareKey = options.key, defaultSortOrder } = options;
        return {
            code: key,
            title: title,
            dataIndex: key,
            sorter: (a: T, b: T) => dayjs(a[compareKey]).unix() - dayjs(b[compareKey]).unix(),
            onFilter: (value, record) => {
                return record[compareKey]
                    ? dayjs(record[compareKey]).isBetween(value[0], value[1])
                    : false;
            },
            filterDropdown: ({ setSelectedKeys, confirm }) => (
                <RangeDateTimePicker
                    allowClear
                    onChange={(e) => {
                        setSelectedKeys(e?.length ? [e as any] : []);
                        confirm();
                    }}
                />
            ),
            render: (text): string => formatDate(text, options.format),
            defaultSortOrder: defaultSortOrder,
            ...options.columnExtraOptions,
        };
    }

    public static createBasicFilterColumn<T>(options: IBasicFilterColumnOptions): IBasicTableColumn {
        const { key, title, searchPlaceholder = 'Search', defaultSortOrder } = options;
        return {
            code: key,
            title: title,
            dataIndex: key,
            textSearchPlaceholder: searchPlaceholder,
            sorter: (a: T, b: T) => a[key]?.localeCompare(b[key]),
            onFilter: (value, record: T) => hasContainsIgnoreCase(new String(record[key])?.toString(), value),
            iconName: 'search',
            defaultSortOrder: defaultSortOrder,
            render: options.render,
            ...options.columnExtraOptions,
        };
    }

    public static createTruncatedTextColumn<T>(options: ITruncatedTextColumnOptions): IBasicTableColumn {
        const { maxWidth = 300, ...columnOptions } = options;

        return this.createBasicFilterColumn<T>({
            ...columnOptions,
            render: (text) => (
                <Typography.Text ellipsis={{ tooltip: text }} style={{ maxWidth, display: 'block' }}>
                    {text}
                </Typography.Text>
            ),
        });
    }

    public static createBasicEnumFilterColumn<T>(options: IBasicEnumFilterColumnOptions): IBasicTableColumn {
        const { key, title, filters, defaultSortOrder, defaultFilteredValue } = options;
        return {
            code: key,
            title: title,
            dataIndex: key,
            filters,
            defaultFilteredValue,
            sorter: (a: T, b: T) => a[key]?.localeCompare(b[key]),
            onFilter: (value, record: T) => localeCompare(record[key], value) === 0,
            defaultSortOrder: defaultSortOrder,
            ...options.columnExtraOptions,
        };
    }
    public static createExtraColumn<T>(options: IExtraColumnOptions): IBasicTableColumn {
        const { key, title, render, defaultSortOrder } = options;
        return {
            code: key,
            title: title,
            dataIndex: key,
            render: render,
            defaultSortOrder: defaultSortOrder,
            ...options.columnExtraOptions,
        };
    }
}

export interface ISearchableTableDataSource<T> {
    onFilter: (searchText: string) => void;
    filteredDataSource: T[];
}

export function useSearchableTableStates<T>(dataSource: T[], filterKeys: (keyof T)[]): ISearchableTableDataSource<T> {
    const [searchText, setSearchText] = React.useState<string>('');
    const filteredDataSource = React.useMemo<T[]>(() => {
        return dataSource.filter(item => {
            return filterKeys.some(filterKey => hasContainsIgnoreCase(new String(item[filterKey])?.toString(), searchText));
        });
    }, [dataSource, searchText]);
    return {
        onFilter: setSearchText,
        filteredDataSource: filteredDataSource
    };
};

export const getRangeDateTimeFilterColumnProps = (dataIndex, format = DATE_TIME_FORMAT.DEFAULT) => ({
    filterDropdown: ({ setSelectedKeys, confirm }) => (
        <RangeDateTimePicker
            allowClear
            onChange={(e) => {
                setSelectedKeys(e?.length ? [e] : []);
                confirm();
            }}
        />
    ),
    onFilter: (value, record): boolean =>
        record[dataIndex] ? dayjs(record[dataIndex]).isBetween(dayjs(value[0]), dayjs(value[1])) : false,
    render: (text): string => formatDate(text, format),
});
